import { API } from '../../apis/limeAuthUtils/apiWrapper';

const API_NAME = 'v2/api-token-manager';
const TOKEN_PATH = '/v2/token';

/**
 * Decode a base64url-encoded JWT segment without verifying the signature.
 * @param {string} segment - base64url encoded string
 * @returns {object} decoded object, or empty object on failure
 */
function decodeBase64UrlSegment(segment) {
    try {
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return {};
    }
}

/**
 * Decode relevant fields from a DDF API token (JWT) without verifying the signature.
 * DDF tokens store `exp` in the header and `description` in the payload.
 * @param {string} token - JWT token string
 * @returns {{ description: string, exp: number|null }} decoded fields
 */
export function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return {};
        const header = decodeBase64UrlSegment(parts[0]);
        const payload = decodeBase64UrlSegment(parts[1]);
        return {
            // exp is stored in the header for DDF tokens
            exp: header.exp ?? payload.exp ?? null,
            // description is stored in the payload
            description: payload.description ?? header.description ?? '',
        };
    } catch {
        return {};
    }
}

export async function getApiTokens() {
    /**
     * function to call api to list api tokens for current user
     * Identity is resolved from OIDC authorizer context
     */
    return await API.get(API_NAME, TOKEN_PATH, {});
}

export async function deleteApiToken(token) {
    /**
     * function to call api to delete api token for current user
     * Identity is resolved from OIDC authorizer context
     * @param token: string
     */
    const myInit = {
        body: {
            token: token,
        },
    };
    return await API.del(API_NAME, TOKEN_PATH, myInit);
}

export async function formatApiTokens() {
    /**
     * function to get formatted api tokens for current user
     * v2 endpoint returns {tokens: ["tok1", "tok2", ...]} - plain token strings
     * Decodes each JWT to extract description and ttl (exp) for display
     */
    const response = await getApiTokens();
    const tokens = response['tokens'] || [];
    return tokens.map((tokenString) => {
        const payload = decodeJwtPayload(tokenString);
        return {
            token: tokenString,
            description: payload.description || '',
            ttl: payload.exp ? String(payload.exp) : '',
        };
    });
}

export async function createApiToken(description) {
    /**
     * function to create api token for user to call apis
     * user/email identity comes from OIDC authorizer context
     * @param description: string
     */
    const myInit = {
        body: {
            description: description,
        },
    };
    return await API.post(API_NAME, TOKEN_PATH, myInit);
}
